/* ──────────────────────────────────────────────────────────────────────
 * DetailDrawer context — apps use `useDetailDrawer()` from any tab.
 *
 * Lives in its own module (not AppShell.tsx) so AppShell exports only
 * components (react-refresh contract); AppShell renders the provider,
 * this file owns the context + consumer hook.
 * ────────────────────────────────────────────────────────────────────── */
import { createContext, useContext } from 'react';

export interface DetailDrawerContextValue<T = unknown> {
  current: T | null;
  open: (item: T) => void;
  close: () => void;
}

export const DetailDrawerContext = createContext<DetailDrawerContextValue | null>(null);

/**
 * useDetailDrawer — open or close the AppShell-managed detail drawer from any
 * tab or nested component. The shell renders the drawer chrome; apps just
 * supply the item.
 *
 * @example
 *   const drawer = useDetailDrawer<Station>();
 *   <TableRow onClick={() => drawer.open(station)}>...</TableRow>
 */
export function useDetailDrawer<T = unknown>(): DetailDrawerContextValue<T> {
  const ctx = useContext(DetailDrawerContext);
  if (!ctx) {
    /* No-op fallback so components don't crash when rendered outside AppShell. */
    return {
      current: null,
      open: () => { /* noop */ },
      close: () => { /* noop */ },
    };
  }
  return ctx as DetailDrawerContextValue<T>;
}
